// UnitStepFrame.js
//
// The frame every step of the unit view is drawn in (Spec 2 section 6.4), and the display
// helpers the steps share. A step is one cleaning process of the unit's chain (or one of
// the fixed steps, Source and Rows after cleaning). Its frame shows, in its header, the
// step's number and name, a chip per kind of problem its process raised, how many of the
// process's decisions a person changed, whether the process is waiting to run again, and
// the "⋯" menu of the two requests; and, in its body, the process's verdicts, the step's
// own content, and the notes Decode left when it dropped a person's change.
//
// A step that edits holds its changes as a draft until Apply (useStepDraft). When the
// table stops being editable while a draft holds changes (Decode starts updating it, after
// someone else's change or another step's Apply; someone approves it), the draft is set
// aside (Spec 2 sections 6.1 and 6.4, 00 N.4): the step shows the table as it now is,
// never applies the draft on its own, and offers it back with Restore once the table can
// be edited again, for the person to check it before applying it. A step's own Apply
// clears its draft first, so the draft just applied is never the one set aside.

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { isPending, personChangeCount, rowKeyText } from '../../../utils/decisions';
import { PROCESS_SUMMARIES, tableScope } from '../../../utils/blobPaths';
import { parseExternalReference } from '../../../utils/externalReferences';
import { splitHeaderPath } from '../../../utils/headerPaths';
import { countNoun } from '../../../utils/pluralise';

// The four columns that carry a row's provenance through cleaning (records.RESERVED_COLUMNS).
// They are never a table column and never shown as one.
export const RESERVED_COLUMNS = ['_row', '_period_label', '_page', '_record'];

const PERIOD = 'period';

// ── Text ────────────────────────────────────────────────────────────────────────────

// A preview or sample row's own key, from its provenance columns.
export function rowKeyOf(row) {
  if (row._page !== undefined && row._page !== null) return { page: row._page, record: row._record };
  if (row._period_label) return { row: row._row, period: row._period_label };
  return { row: row._row };
}

// One text per row key, whatever the order of its fields, for drafts and React keys.
export function rowKeyId(key) {
  return JSON.stringify(Object.keys(key).sort().map((name) => [name, key[name]]));
}

// The rows a verdict names: its first few, and how many more there are, as in
// "row 9, row 12 and 3 more". A row is named by its key ('row 9', 'p3 #4'), or by its
// identifier when the check against the table named it by one ('POL-3001').
export function verdictRowsText(verdict) {
  const rows = verdict.rows || [];
  if (rows.length === 0) return '';
  const named = rows.map((row) => (row.identifier ? row.identifier : rowKeyText(row))).join(', ');
  const more = (verdict.row_count || rows.length) - rows.length;
  return more > 0 ? `${named} and ${more} more` : named;
}

// A table column as the person reads it: 'period' is the reporting period, a metric
// leaf loses its period prefix, a nested leaf shows its group, underscores are spaces.
export function targetLabel(target) {
  if (target === PERIOD) return 'Reporting period';
  const path = target.startsWith(`${PERIOD}.`) ? target.slice(PERIOD.length + 1) : target;
  return path.split('.').map((part) => part.replace(/_/g, ' ')).join(' › ');
}

// The date FileNameDate reads from the file's name, which the pool shares under the Doc
// scope for a workbook as for a document (file_processes.FILE_NAME_DATE_REFERENCE).
const FILE_NAME_DATE_KEY = 'File Name Date';

// A mapping source as the person reads it: a header field by its name, a shared value
// by the sheet (or the document) it came from, a column by its header path.
export function sourceLabel(source) {
  if (source === null || source === undefined || source === '') return 'Not present';
  const text = String(source);
  if (text.startsWith('External >> ')) {
    const { scope, sourceName, key } = parseExternalReference(text);
    if (scope === 'Sheet') return `${key} (header field)`;
    if (scope === 'Doc' && key === FILE_NAME_DATE_KEY) return 'The date in the file name';
    if (scope === 'Doc') return `${key} (from the document)`;
    return `${sourceName} › ${key}`;
  }
  return splitHeaderPath(text).join(' › ');
}

// A cell's value as text; an empty cell is a dash.
export function cellText(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

// ── Storage ─────────────────────────────────────────────────────────────────────────

// The one folder a unit's summaries and spilled records may be read from. A pointer is
// read off a browser-writable record, so it is followed only inside the unit's own
// folder: anything else could name another unit's, or another tenant's, blob.
export function processSummaryFolder(companyId, tableId, unitId) {
  return `${tableScope(companyId, tableId)}/${PROCESS_SUMMARIES}/${unitId}/`;
}

export function isInFolder(path, folder) {
  return typeof path === 'string' && path.startsWith(folder) && !path.split('/').includes('..');
}

// ── Pieces ──────────────────────────────────────────────────────────────────────────

const REQUESTS = [
  { kind: 'reapply', label: 'Redo this step', hint: 'Decode looks again, keeping your changes' },
  { kind: 'start_over', label: 'Start this step over', hint: 'Decode decides from scratch; your changes here are forgotten' },
];

function StepMenu({ title, onRequest }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <IconButton
        size="small"
        aria-label={`More for ${title}`}
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {REQUESTS.map((request) => (
          <MenuItem
            key={request.kind}
            onClick={() => {
              setAnchor(null);
              onRequest(request.kind);
            }}
          >
            <ListItemText primary={request.label} secondary={request.hint} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// A verdict as an alert: the message, and the rows it names.
export function VerdictAlert({ verdict }) {
  const rows = verdictRowsText(verdict);
  return (
    <Alert severity={verdict.level === 'fail' ? 'error' : 'warning'} sx={{ mb: 1 }}>
      {verdict.message}
      {rows && <Typography variant="caption" sx={{ display: 'block' }}>{rows}</Typography>}
    </Alert>
  );
}

// The bar under a step whose draft has changes: how many, Discard and Apply. While the
// unit cannot be edited the draft is kept, and the bar says why it cannot be applied.
export function ApplyBar({ changeCount, editBlock, applying, applyDisabledReason = '', onApply, onDiscard }) {
  if (changeCount === 0) return null;
  const reason = editBlock || applyDisabledReason;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
      <Typography variant="body2">{countNoun(changeCount, 'change')}</Typography>
      {reason && (
        <Typography variant="caption" color="text.secondary">
          {editBlock ? `Kept, but not applied: ${editBlock}` : reason}
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      <Button size="small" onClick={onDiscard} disabled={applying} sx={{ textTransform: 'none' }}>Discard</Button>
      <Button
        size="small"
        variant="contained"
        onClick={onApply}
        disabled={applying || Boolean(reason)}
        sx={{ textTransform: 'none' }}
      >
        {applying ? <CircularProgress size={16} /> : 'Apply'}
      </Button>
    </Box>
  );
}

// ── Drafts ──────────────────────────────────────────────────────────────────────────

// A step's draft of changes, bound to the unit view's draft epoch. The view moves the
// epoch on each time the table stops being editable, and a draft made in an earlier epoch
// is set aside: the step neither shows nor applies it until the person restores it into
// the current epoch, or discards it. `noChanges` is the step's empty draft.
//
//   changes          the draft the step shows and applies (noChanges while one is set aside)
//   setAsideChanges  the set-aside draft, or null when there is none
//   change(update)   the draft becomes update(changes); over a set-aside draft it starts afresh
//   clear()          an empty draft, after Apply or Discard
//   restore()        the set-aside draft, back in the current epoch
export function useStepDraft(draftEpoch, noChanges) {
  const [draft, setDraft] = useState({ epoch: draftEpoch, changes: noChanges });
  const current = draft.epoch === draftEpoch;
  return {
    changes: current ? draft.changes : noChanges,
    setAsideChanges: current ? null : draft.changes,
    change: (update) => setDraft((previous) => ({
      epoch: draftEpoch,
      changes: update(previous.epoch === draftEpoch ? previous.changes : noChanges),
    })),
    clear: () => setDraft({ epoch: draftEpoch, changes: noChanges }),
    restore: () => setDraft((previous) => ({ ...previous, epoch: draftEpoch })),
  };
}

// What a step shows over a set-aside draft holding `changeCount` changes: why it was set
// aside, Restore, which waits until the table can be edited again, and Discard.
export function SetAsideNotice({ changeCount, editBlock, onRestore, onDiscard }) {
  if (changeCount === 0) return null;
  const them = changeCount === 1 ? 'it' : 'them';
  return (
    <Alert
      severity="info"
      sx={{ mb: 1 }}
      action={(
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button color="inherit" size="small" onClick={onDiscard} sx={{ textTransform: 'none' }}>Discard</Button>
          <Button
            color="inherit"
            size="small"
            disabled={Boolean(editBlock)}
            onClick={onRestore}
            sx={{ textTransform: 'none' }}
          >
            Restore
          </Button>
        </Box>
      )}
    >
      {`Your ${countNoun(changeCount, 'unapplied change')} ${changeCount === 1 ? 'was' : 'were'} set aside `
        + 'when this table stopped being editable. '}
      {editBlock
        ? `Restore ${them} once it can be edited again.`
        : `Restore ${them} to check ${them} against the table as it is now, or discard ${them}.`}
    </Alert>
  );
}

// Props:
//   number, title       the step's place in the view and its plain name
//   verdicts            the verdicts this step's process raised
//   record              the process's record on the unit, or null for a step with none
//   menu                whether the step offers "Redo this step" / "Start this step over"
//   editBlock           why the unit cannot be edited now ('' when it can); hides the menu
//   onRequest           (kind) => void, for the menu
//   defaultOpen         whether the step starts open
export default function UnitStepFrame({
  number,
  title,
  verdicts = [],
  record = null,
  menu = false,
  editBlock = '',
  onRequest = null,
  defaultOpen = false,
  children,
}) {
  // The person's own open or closed choice wins; until they make one the step follows
  // defaultOpen, which can change as the unit's verdicts do.
  const [chosenOpen, setChosenOpen] = useState(null);
  const open = chosenOpen === null ? defaultOpen : chosenOpen;

  const failCount = verdicts.filter((verdict) => verdict.level === 'fail').length;
  const warnCount = verdicts.filter((verdict) => verdict.level === 'warn').length;
  const yours = record ? personChangeCount(record) : 0;
  const pending = record ? isPending(record) : false;
  const dropped = record?.dropped || [];

  return (
    <Paper
      variant="outlined"
      data-testid={`step-${number}`}
      sx={{ mb: 1, borderColor: failCount ? 'error.light' : warnCount ? 'warning.light' : 'divider' }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setChosenOpen(!open)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setChosenOpen(!open);
          }
        }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer' }}
      >
        <Box sx={{
          width: 22, height: 22, borderRadius: '50%', bgcolor: 'action.selected', flex: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
        }}
        >
          {number}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
        {failCount > 0 && <Chip size="small" color="error" label={countNoun(failCount, 'problem')} />}
        {warnCount > 0 && <Chip size="small" color="warning" variant="outlined" label={countNoun(warnCount, 'warning')} />}
        {yours > 0 && <Chip size="small" color="primary" variant="outlined" label={`${countNoun(yours, 'change')} by you`} />}
        {pending && (
          <Chip
            size="small"
            color="info"
            icon={<CircularProgress size={12} />}
            label={record.requested === 'start_over' ? 'starting over…' : 'updating…'}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {menu && record && !editBlock && onRequest && <StepMenu title={title} onRequest={onRequest} />}
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {/* Neither a verdict nor a dropped note holds state of its own, so its
              position is a good enough key. */}
          {verdicts.map((verdict, index) => (
            <VerdictAlert key={index} verdict={verdict} />
          ))}
          {children}
          {dropped.map((note, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mt: 1 }}>
              <WarningAmberIcon color="warning" sx={{ fontSize: 16, mt: '2px' }} />
              <Typography variant="caption" color="text.secondary">{note.reason}</Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}
